import { isClickedOutside } from '@theme/utilities';

/* Ouverture au survol : uniquement sur ordinateur (souris + écran > 1000px) */
const canHover = () =>
  window.matchMedia('(hover: hover) and (pointer: fine)').matches && window.innerWidth > 1000;
const HOVER_CLOSE_DELAY = 200;

class DropdownGroupComponent extends HTMLElement {
  constructor() {
    super();

    this.dropdowns = [];
  }

  connectedCallback() {
    this.dropdowns = this.querySelectorAll('dropdown-component');

    document.addEventListener('click', this.#handleDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#handleDocumentClick);
  }

  #handleDocumentClick = (event) => {
    const clickedInsideDropdown = event.target.closest('dropdown-component');
    if (!clickedInsideDropdown) {
      this.closeAllDropdowns();
    }
  };

  closeAllDropdowns(except = null) {
    this.dropdowns.forEach((dropdown) => {
      if (dropdown !== except) dropdown.close();
    });
  }
}

if (!customElements.get('dropdown-group-component')) {
  customElements.define('dropdown-group-component', DropdownGroupComponent);
}

class DropdownComponent extends HTMLElement {
  #closeTimer = null;

  constructor() {
    super();

    this.toggle = null;
    this.content = null;
    this.isOpen = false;
  }

  connectedCallback() {
    this.toggle = this.querySelector('[data-ref="dropdown-toggle"]');
    this.content = this.querySelector('[data-ref="dropdown-content"]');

    this.toggle?.addEventListener('click', this.#handleToggle);
    this.addEventListener('mouseenter', this.#handleMouseEnter);
    this.addEventListener('mouseleave', this.#handleMouseLeave);
    this.addEventListener('focusout', this.#handleFocusOut);
  }

  disconnectedCallback() {
    this.toggle?.removeEventListener('click', this.#handleToggle);
    this.removeEventListener('mouseenter', this.#handleMouseEnter);
    this.removeEventListener('mouseleave', this.#handleMouseLeave);
    this.removeEventListener('focusout', this.#handleFocusOut);
    this.#clearCloseTimer();
  }

  #clearCloseTimer() {
    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
  }

  #handleMouseEnter = () => {
    if (!canHover()) return;
    this.#clearCloseTimer();
    if (!this.isOpen) {
      this.closest('dropdown-group-component')?.closeAllDropdowns(this);
      this.open();
    }
  };

  #handleMouseLeave = () => {
    if (!canHover()) return;
    this.#clearCloseTimer();
    this.#closeTimer = setTimeout(() => this.close(), HOVER_CLOSE_DELAY);
  };

  #handleFocusOut = (event) => {
    if (!canHover()) return;
    if (event.relatedTarget && !this.contains(event.relatedTarget)) {
      this.close();
    }
  };

  #handleToggle = (event) => {
    event.preventDefault();
    event.stopPropagation();

    /* Sur ordinateur, le survol a déjà ouvert le menu : le clic ne le referme pas */
    if (canHover() && this.isOpen && this.matches(':hover')) return;

    if (this.isOpen) {
      this.close();
    } else {
      this.closest('dropdown-group-component')?.closeAllDropdowns(this);
      this.open();
    }
  };

  #handleClickOutside = (event) => {
    if (isClickedOutside(event, this)) {
      this.close();
    }
  };

  open() {
    this.#clearCloseTimer();
    this.setAttribute('data-open', 'true');
    this.toggle?.setAttribute('aria-expanded', 'true');
    document.addEventListener('click', this.#handleClickOutside);
    this.isOpen = true;
  }

  close() {
    this.#clearCloseTimer();
    this.setAttribute('data-open', 'false');
    this.toggle?.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', this.#handleClickOutside);
    this.isOpen = false;
  }
}

if (!customElements.get('dropdown-component')) {
  customElements.define('dropdown-component', DropdownComponent);
}
